import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Input, InputNumber, Space, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { Dispatch, SetStateAction } from "react";
import { formatInr } from "../../shared/utils/currency";
import { formatDate } from "../../shared/utils/date";
import { PersistedTable } from "../../shared/components/PersistedTable";
import type { AllocationRow, LineRow, PendingBill, PreviewInfo } from "./voucherEntryUtils";
import { newLine } from "./voucherEntryUtils";

export function VoucherInvoiceLines({
  lines,
  preview,
  totals,
  updateLine,
  setLines,
  setDirty
}: {
  lines: LineRow[];
  preview: PreviewInfo | null;
  totals: { taxable: number; gst: number; tds: number; grand: number };
  updateLine: (key: string, patch: Partial<LineRow>) => void;
  setLines: Dispatch<SetStateAction<LineRow[]>>;
  setDirty: (dirty: boolean) => void;
}) {
  const columns: ColumnsType<LineRow> = [
    { title: "#", width: 48, render: (_, __, index) => index + 1 },
    { title: "Item / Ledger", dataIndex: "ledgerName", render: (_, row) => <Input value={row.ledgerName} onChange={(e) => updateLine(row.key, { ledgerName: e.target.value })} /> },
    { title: "SAC", width: 120, render: (_, row) => <Input value={row.sacCode} onChange={(e) => updateLine(row.key, { sacCode: e.target.value })} /> },
    { title: "Qty", width: 110, render: (_, row) => <InputNumber value={row.quantity} min={0} onChange={(v) => { const quantity=Number(v??0);updateLine(row.key,{quantity,amount:Number((quantity*Number(row.rate??0)).toFixed(2))}); }} style={{ width: "100%" }} /> },
    { title: "Rate", width: 130, render: (_, row) => <InputNumber value={row.rate} onChange={(v) => { const rate=Number(v??0);updateLine(row.key,{rate,amount:Number((Number(row.quantity??0)*rate).toFixed(2))}); }} style={{ width: "100%" }} /> },
    { title: "Per", width: 100, render: (_, row) => <Input value={row.per} onChange={(e) => updateLine(row.key, { per: e.target.value })} /> },
    { title: "GST %", width: 100, render: (_, row) => <InputNumber value={row.gstPercent} min={0} max={100} onChange={(v) => updateLine(row.key, { gstPercent: Number(v ?? 0) })} style={{ width: "100%" }} /> },
    { title: "TDS %", width: 100, render: (_, row) => <InputNumber value={row.tdsPercent} min={0} max={100} onChange={(v) => updateLine(row.key, { tdsPercent: Number(v ?? 0) })} style={{ width: "100%" }} /> },
    { title: "Remarks", width: 180, render: (_, row) => <Input.TextArea autoSize={{ minRows: 1, maxRows: 3 }} value={row.remarks} onChange={(e) => updateLine(row.key, { remarks: e.target.value })} /> },
    { title: "Amount", width: 140, align: "right", render: (_, row) => formatInr(invoiceLineAmount(row)) },
    { title: "", width: 48, render: (_, row) => <Button danger size="small" icon={<DeleteOutlined />} onClick={() => { setDirty(true); setLines((rows) => rows.length > 1 ? rows.filter((x) => x.key !== row.key) : rows); }} /> }
  ];

  return (
    <>
      <PersistedTable tableId="voucher-invoice-lines" rowKey="key" size="small" bordered pagination={false} columns={columns} dataSource={lines} scroll={{ x: 1350 }} />
      <div className="voucher-line-footer">
        <Button icon={<PlusOutlined />} onClick={() => { setDirty(true); setLines((rows) => [...rows, newLine()]); }}>Add Line</Button>
        <Space wrap>
          <Tag color="default">Taxable {formatInr(preview?.taxableAmount ?? totals.taxable)}</Tag>
          <Tag color="cyan">GST {formatInr((preview?.cgstTotal ?? 0) + (preview?.sgstTotal ?? 0) + (preview?.igstTotal ?? totals.gst))}</Tag>
          <Tag color="orange">TDS {formatInr(preview?.tdsTotal ?? totals.tds)}</Tag>
          <Tag color="blue">Grand {formatInr(preview?.grandTotal ?? totals.grand)}</Tag>
        </Space>
      </div>
    </>
  );
}

function invoiceLineAmount(row: LineRow) {
  const explicit = Number(row.amount ?? 0);
  return explicit !== 0 ? explicit : Number(row.quantity ?? 0) * Number(row.rate ?? 0);
}

export function PendingBillsAllocationCard({
  partySelected,
  pendingBills,
  allocations,
  setAllocations,
  setDirty
}: {
  partySelected: boolean;
  pendingBills: { isLoading: boolean; data?: PendingBill[] };
  allocations: AllocationRow[];
  setAllocations: Dispatch<SetStateAction<AllocationRow[]>>;
  setDirty: (dirty: boolean) => void;
}) {
  const availableBills = (pendingBills.data ?? []).filter((row) => !allocations.some((allocation) => allocation.billId === row.id));

  return (
    <Card className="section-card" title="Pending Bills">
      {!partySelected ? <Alert showIcon type="info" message="Select a party to load pending bills." className="voucher-inline-alert" /> : null}
      <PersistedTable
        tableId="voucher-pending-bills"
        rowKey="id"
        size="small"
        loading={pendingBills.isLoading}
        dataSource={availableBills}
        pagination={false}
        columns={[
          { title: "Bill No", dataIndex: "billNo" },
          { title: "Date", dataIndex: "billDate", render: formatDate },
          { title: "Voucher", dataIndex: "voucherType" },
          { title: "Total", dataIndex: "grandTotal", align: "right", render: formatInr },
          { title: "Paid", dataIndex: "paidAmount", align: "right", render: formatInr },
          { title: "Outstanding", dataIndex: "outstanding", align: "right", render: formatInr },
          { title: "Type", dataIndex: "balanceType", width: 80 },
          {
            title: "Allocate",
            width: 170,
            render: (_, row) => {
              const existing = allocations.find((x) => x.billId === row.id);
              return (
                <InputNumber
                  min={0}
                  max={row.outstanding}
                  value={existing?.allocatedAmount}
                  onChange={(v) => setAllocations((items) => {
                    setDirty(true);
                    const next = items.filter((x) => x.billId !== row.id);
                    const amount = Number(v ?? 0);
                    return amount > 0 ? [...next, { billId: row.id, allocatedAmount: amount }] : next;
                  })}
                  style={{ width: "100%" }}
                />
              );
            }
          },
          {
            title: "",
            width: 90,
            render: (_, row) => (
              <Button size="small" onClick={() => {
                setDirty(true);
                setAllocations((items) => [...items.filter((x) => x.billId !== row.id), { billId: row.id, allocatedAmount: row.outstanding }]);
              }}>
                Full
              </Button>
            )
          }
        ]}
      />
      <div className="voucher-line-footer">
        <Tag color="blue">Allocated {formatInr(allocations.reduce((sum, row) => sum + Number(row.allocatedAmount ?? 0), 0))}</Tag>
      </div>
    </Card>
  );
}
